/**
 * insertParseHelpers — shared low-level token utilities for the INSERT renderer
 * extractor. Split out so `extractInsertOutline.ts` can stay focused on the
 * statement-shape walker. The same primitives (readWord / readIdentifier /
 * skipWsAndComments / consumeParenGroup) are duplicated across other VQE-RO
 * parser helpers (e.g. `extractCtes.ts`); future consolidation work could move
 * these to a shared `parserUtils` module — out of scope for this slice.
 */

import type { SkipContext } from '../../../../sql-resolver/classify/tokenSkip';
import { trySkipNonCode } from '../../../../sql-resolver/classify/tokenSkip';

export const IDENT_START = /[A-Za-z_]/;
export const IDENT_CONT = /[A-Za-z0-9_$]/;

export interface IdentifierMatch {
  readonly start: number;
  readonly end: number;
}

export interface WordMatch {
  readonly upper: string;
  readonly start: number;
  readonly end: number;
}

export function readIdentifier(source: string, from: number, end: number): IdentifierMatch | null {
  if (from >= end) return null;
  const c = source[from];
  if (c === '"') {
    let j = from + 1;
    while (j < end) {
      if (source[j] === '"') {
        if (source[j + 1] === '"') { j += 2; continue; }
        return { start: from, end: j + 1 };
      }
      j++;
    }
    return null;
  }
  if (c === '`') {
    let j = from + 1;
    while (j < end && source[j] !== '`') j++;
    if (j >= end) return null;
    return { start: from, end: j + 1 };
  }
  if (c === '[') {
    let j = from + 1;
    while (j < end && source[j] !== ']') j++;
    if (j >= end) return null;
    return { start: from, end: j + 1 };
  }
  if (!IDENT_START.test(c)) return null;
  let j = from + 1;
  while (j < end && IDENT_CONT.test(source[j])) j++;
  return { start: from, end: j };
}

export function readWord(source: string, from: number, end: number): WordMatch | null {
  if (from >= end || !IDENT_START.test(source[from])) return null;
  let j = from + 1;
  while (j < end && IDENT_CONT.test(source[j])) j++;
  return { upper: source.slice(from, j).toUpperCase(), start: from, end: j };
}

export function skipWsAndComments(source: string, from: number, end: number): number {
  let i = from;
  while (i < end) {
    const c = source[i];
    if (/\s/.test(c)) { i++; continue; }
    const c2 = source[i + 1] ?? '';
    if (c === '-' && c2 === '-') {
      let j = i + 2;
      while (j < end && source[j] !== '\n') j++;
      i = j;
      continue;
    }
    if (c === '/' && c2 === '*') {
      let j = i + 2;
      while (j < end && !(source[j] === '*' && source[j + 1] === '/')) j++;
      if (j < end) j += 2;
      i = j;
      continue;
    }
    break;
  }
  return i;
}

export function consumeParenGroup(source: string, start: number, end: number, ctx: SkipContext): number {
  let depth = 0;
  let i = start;
  while (i < end) {
    const skipped = trySkipNonCode(source, i, end, ctx);
    if (skipped >= 0) { i = skipped; continue; }
    if (source[i] === '(') { depth++; i++; continue; }
    if (source[i] === ')') { depth--; i++; if (depth === 0) return i; continue; }
    i++;
  }
  return i;
}

export function readColumnList(source: string, start: number, end: number, ctx: SkipContext): string[] {
  const out: string[] = [];
  let i = start;
  let segStart = start;
  let paren = 0;
  while (i < end) {
    const skipped = trySkipNonCode(source, i, end, ctx);
    if (skipped >= 0) { i = skipped; continue; }
    const c = source[i];
    if (c === '(') { paren++; i++; continue; }
    if (c === ')') { if (paren > 0) paren--; i++; continue; }
    if (c === ',' && paren === 0) {
      const piece = source.slice(segStart, i).trim();
      if (piece.length > 0) out.push(stripIdentifierQuotes(piece));
      segStart = i + 1;
      i++;
      continue;
    }
    i++;
  }
  const tail = source.slice(segStart, end).trim();
  if (tail.length > 0) out.push(stripIdentifierQuotes(tail));
  return out;
}

export function splitTopLevelCommasToText(
  source: string,
  start: number,
  end: number,
  ctx: SkipContext,
): string[] {
  const out: string[] = [];
  let i = start;
  let segStart = start;
  let paren = 0;
  while (i < end) {
    const skipped = trySkipNonCode(source, i, end, ctx);
    if (skipped >= 0) { i = skipped; continue; }
    const c = source[i];
    if (c === '(') { paren++; i++; continue; }
    if (c === ')') { if (paren > 0) paren--; i++; continue; }
    if (c === ',' && paren === 0) {
      out.push(source.slice(segStart, i).trim());
      segStart = i + 1;
      i++;
      continue;
    }
    i++;
  }
  out.push(source.slice(segStart, end).trim());
  return out;
}

export function stripIdentifierQuotes(raw: string): string {
  if (raw.length >= 2) {
    const first = raw[0];
    const last = raw[raw.length - 1];
    if (first === '"' && last === '"') return raw.slice(1, -1).replace(/""/g, '"');
    if (first === '`' && last === '`') return raw.slice(1, -1);
    if (first === '[' && last === ']') return raw.slice(1, -1);
  }
  return raw;
}

export function isWordBoundaryBefore(source: string, index: number): boolean {
  if (index === 0) return true;
  const prev = source[index - 1];
  return !IDENT_CONT.test(prev);
}
